<script setup lang="ts">
import { type Category, CategoryType } from "mws-ts-v2-sdk";
import type { OfferCatalog } from "schema-dts";

import { OG_IMAGE_SIZE } from "@/constants/metaTags";

import type { CategoryPageStoryblok } from "~/storyblok/.generated/component-types";

const { category, relatedCategories, metaTags } = defineProps<{
  category: Category;
  metaTags?: CategoryPageStoryblok["metaTags"];
  relatedCategories?: Category[];
}>();

const platformStore = usePlatformStore();
const platformName = platformStore.getPlatformName;
const isAuctionsEnabled =
  platformStore.getPlatformConfiguration.isAuctionsEnabled;
const i18n = useI18n();

const sportCategory = computed(() =>
  relatedCategories?.find(
    (c) => c.externalReference?.type === CategoryType.Sport,
  ),
);

const isFootballTeamPage = computed(
  () =>
    sportCategory.value?.name === "Football" &&
    category.externalReference?.type === CategoryType.Team,
);

const categoryName = computed(() => category.name);

const title = computed(() => {
  if (metaTags?.title) return metaTags.title;

  if (isAuctionsEnabled && isFootballTeamPage.value) {
    return i18n.t("category.metaTags.footballTeams.title", {
      categoryName: categoryName.value,
    });
  }

  return `${categoryName.value} | ${platformName}`;
});

const ogTitle = computed(() => {
  if (metaTags?.og_title) return metaTags.og_title;

  return title.value;
});

const description = computed(() => {
  if (metaTags?.description) return metaTags.description;

  if (isAuctionsEnabled) {
    if (isFootballTeamPage.value)
      return i18n.t("category.metaTags.footballTeams.description", {
        categoryName: categoryName.value,
      });

    return i18n.t("category.metaTags.defaultDescription", {
      PLACEHOLDER_categoryName: categoryName.value,
    });
  }

  return i18n.t("category.metaTags.noAuctionDefaultDescription", {
    PLACEHOLDER_categoryName: categoryName.value,
  });
});

const ogDescription = computed(() => {
  if (metaTags?.og_description) return metaTags.og_description;

  return description.value;
});

const ogImage = computed(() => {
  if (metaTags?.og_image) return metaTags.og_image;

  const platformLogo =
    platformStore.getPlatformConfiguration.assets.primaryLogo;

  return `${platformLogo}?w=${OG_IMAGE_SIZE}&h=${OG_IMAGE_SIZE}&fit=contain`;
});

const { t } = useI18n();

const localePath = useLocalePath();
const activePath = computed(() => localePath(`/category/${category.slug}`));

const OFFER_CATALOG_STRUCTURED_DATA: OfferCatalog = {
  "@type": "OfferCatalog",
  name: t("category.structuredData.footballTeams.name", {
    categoryName: categoryName.value,
  }),
  url: `https://${platformStore.getPlatformConfiguration.primaryDomain}${activePath.value}`,
  description: t("category.structuredData.footballTeams.description", {
    categoryName: categoryName.value,
  }),
};

useSchemaOrg([OFFER_CATALOG_STRUCTURED_DATA]);
</script>

<template>
  <LayoutMetaTags
    :meta-tags="{
      title,
      og_title: ogTitle,
      description,
      og_description: ogDescription,
      og_image: ogImage ?? undefined,
    }"
  />
</template>
